import React from 'react';
import { 
  Award, 
  Briefcase, 
  GraduationCap, 
  MapPin, 
  Clock, 
  Users, 
  Cpu, 
  ArrowRight
} from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-16">
      <div className="container mx-auto px-6">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">About Me</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Platforms Engineer specializing in building scalable, resilient infrastructure 
            with expertise in AWS, GCP, and modern data architectures.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Profile Image */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="h-64 bg-gradient-to-r from-blue-500 to-teal-400 flex items-center justify-center">
                {/* Placeholder for profile image */}
                <img 
                    src="/assets/images/profile_photo.JPG" 
                    alt="PC" 
                    className="h-60 w-60 rounded-full"
                />
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Philip Crilley</h2>
                <p className="text-gray-600 mb-4">Platforms Engineer & Big Data Specialist</p>
                
                <div className="space-y-3">
                  <div className="flex items-center">
                    <MapPin size={18} className="text-blue-500 mr-2" />
                    <span className="text-gray-700">Alexandria, Virginia</span>
                  </div>
                  <div className="flex items-center">
                    <Briefcase size={18} className="text-blue-500 mr-2" />
                    <span className="text-gray-700">Syntasa (Current)</span>
                  </div>
                  <div className="flex items-center">
                    <Clock size={18} className="text-blue-500 mr-2" />
                    <span className="text-gray-700">8+ Years Experience</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Professional Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Professional Summary</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  I'm a platforms engineering professional with over 8 years of experience specializing in building 
                  scalable infrastructure and data solutions. My expertise spans multiple cloud platforms including 
                  AWS, GCP, and Azure with a focus on infrastructure automation, containerization, and data engineering.
                </p>
                <p>
                  At Syntasa, I've been leading cloud automation initiatives and building multi-cloud deployment 
                  capabilities for data warehousing platforms. I work at the intersection of infrastructure, 
                  data, and security, ensuring that systems are not only performant but also maintainable and secure.
                </p>
                <p>
                  My approach combines technical expertise with a focus on business outcomes. I believe that 
                  infrastructure should enable innovation rather than constrain it, and I'm passionate about 
                  creating solutions that provide real value while maintaining operational excellence.
                </p>
                <p>
                  When I'm not optimizing cloud infrastructure, I enjoy hiking in the Appalachian Mountains, Snowboarding,
                  and contributing to open-source projects
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Experience Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Professional Experience</h2>
          
          <div className="space-y-8">
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/4 bg-blue-50 p-6 flex items-center justify-center">
                  <div className="text-center">
                    <Briefcase size={40} className="text-blue-500 mx-auto mb-2" />
                    <span className="block font-bold text-gray-800">Jul 2022 - Present</span>
                  </div>
                </div>
                <div className="md:w-3/4 p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Platforms Engineer - Big Data</h3>
                  <h4 className="text-lg text-blue-600 mb-4">Syntasa</h4>
                  <div className="space-y-3 text-gray-700">
                    <p>
                      Leading cloud automation and multi-cloud infrastructure projects for Syntasa's data 
                      warehousing platform, supporting deployments across AWS and GCP environments.
                    </p>
                    <div>
                      <h5 className="font-semibold mb-2">Key Achievements:</h5>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Built terraform automation modules supporting both AWS and GCP deployments</li>
                        <li>Enhanced infrastructure to support self-managed Kubernetes as an alternative to EKS</li>
                        <li>Expanded Spark workload capabilities to run on Kubernetes instead of EMR/Dataproc</li>
                        <li>Designed GCP data warehouse architecture for state government modernization project</li>
                        <li>Developed hub-and-spoke model for Airflow workflow deployments</li>
                        <li>Improved pipeline maturity and standardized API architecture</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/4 bg-blue-50 p-6 flex items-center justify-center">
                  <div className="text-center">
                    <Briefcase size={40} className="text-blue-500 mx-auto mb-2" />
                    <span className="block font-bold text-gray-800">December 2020 - Jun 2022</span>
                  </div>
                </div>
                <div className="md:w-3/4 p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Software Engineer - Team Lead</h3>
                  <h4 className="text-lg text-blue-600 mb-4">Guidehouse</h4>
                  <div className="space-y-3 text-gray-700">
                  <p>
                    Led the Data Visualization team, focusing on application modernization, API standardization, 
                    and deployment automation while coordinating with DevOps and authentication teams.
                  </p>
                  <div>
                  <h5 className="font-semibold mb-2">Key Achievements:</h5>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Refactored and standardized API architecture, reducing complexity and decoupling UI from backend</li>
                    <li>Enhanced CI/CD pipeline maturity and improved application deployment structure across environments</li>
                    <li>Collaborated with lead developers to refactor permission system integration with Portal authentication</li>
                    <li>Established foundation for unit testing and Katalon/Selenium testing with QA team</li>
                    <li>Conducted security scans (Fortify, Webinspect, DbProtect) and created remediation plans</li>
                    <li>Coordinated with transactional team leads to improve overall data architecture</li>
                  </ul>
                </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="md:flex">
                <div className="md:w-1/4 bg-blue-50 p-6 flex items-center justify-center">
                  <div className="text-center">
                    <Briefcase size={40} className="text-blue-500 mx-auto mb-2" />
                    <span className="block font-bold text-gray-800">Jan 2018 - December 2020</span>
                  </div>
                </div>
                <div className="md:w-3/4 p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Cloud Engineer</h3>
                  <h4 className="text-lg text-blue-600 mb-4">YOM - Amazon Powerseller</h4>
                  <div className="space-y-3 text-gray-700">
                    <p>
                      Responsible for leading the migration from DigitalOcean PaaS to AWS SaaS architecture, 
                      implementing serverless infrastructure, CI/CD automation, and modernizing legacy applications.
                    </p>
                    <div>
                    <h5 className="font-semibold mb-2">Key Achievements:</h5>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Architected serverless solutions using API Gateway, Lambda functions, CloudFront, and S3 buckets</li>
                        <li>Implemented infrastructure as code using AWS SAM and CloudFormation</li>
                        <li>Enhanced security with Cognito integration for both UI and API authentication</li>
                        <li>Established automated deployment pipelines using CodePipeline for both frontend and backend</li>
                        <li>Refactored legacy jQuery UI into a modern React application, improving maintainability</li>
                        <li>Implemented git hooks for automated security scans, unit testing, and code quality analysis (CodeScene)</li>
                    </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* College Student Tech Sr - Intern at Leidos */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
            <div className="md:flex">
                <div className="md:w-1/4 bg-blue-50 p-6 flex items-center justify-center">
                <div className="text-center">
                    <Briefcase size={40} className="text-blue-500 mx-auto mb-2" />
                    <span className="block font-bold text-gray-800">Jun 2017 - Aug 2017</span>
                    <span className="block font-bold text-gray-800">Jun 2018 - Aug 2018</span>
                </div>
                </div>
                <div className="md:w-3/4 p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">College Student Tech Sr - Intern</h3>
                <h4 className="text-lg text-blue-600 mb-4">Leidos</h4>
                <h5 className="text-base text-gray-600 mb-4">Maryland</h5>
                <div className="space-y-3 text-gray-700">
                    <p>
                      Supported technical operations through a structured internship program, focusing on 
                      IT infrastructure and technical support for internal teams.
                    </p>
                    <div>
                    <h5 className="font-semibold mb-2">Key Responsibilities:</h5>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Assisted with IT support tickets and technical troubleshooting</li>
                        <li>Helped maintain and configure computer systems and networks</li>
                        <li>Documented technical processes and created user guides</li>
                        <li>Shadowed senior IT professionals to learn enterprise infrastructure management</li>
                    </ul>
                    </div>
                </div>
                </div>
            </div>
            </div>

            {/* Full Stack Developer at YourOnlineMarketplace */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
            <div className="md:flex">
                <div className="md:w-1/4 bg-blue-50 p-6 flex items-center justify-center">
                <div className="text-center">
                    <Briefcase size={40} className="text-blue-500 mx-auto mb-2" />
                    <span className="block font-bold text-gray-800">Jan 2014 - Jan 2017</span>
                </div>
                </div>
                <div className="md:w-3/4 p-6">
                <h3 className="text-xl font-bold text-gray-800 mb-2">Full Stack Developer</h3>
                <h4 className="text-lg text-blue-600 mb-4">YOM eBay and Amazon Powerseller</h4>
                <h5 className="text-base text-gray-600 mb-4">New York</h5>
                <div className="space-y-3 text-gray-700">
                    <p>
                      Developed and maintained e-commerce automation tools and applications to optimize 
                      online marketplace operations for an eBay and Amazon powerseller.
                    </p>
                    <div>
                    <h5 className="font-semibold mb-2">Key Projects:</h5>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Created a price monitoring and comparison tool to track competitor pricing</li>
                        <li>Developed an eBay listing generator to streamline product uploads</li>
                        <li>Built an eBay bulk repricing tool with an automated algorithm to optimize pricing</li>
                        <li>Implemented an automated order processor using Selenium to reduce manual operations</li>
                        <li>Designed and maintained a database for eBay listing information with historical and current competitor price data</li>
                    </ul>
                    </div>
                </div>
                </div>
            </div>
            </div>
          </div>
        </div>
        
        {/* Education & Certifications */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center mb-6">
              <GraduationCap size={24} className="text-blue-500 mr-3" />
              <h2 className="text-2xl font-bold text-gray-800">Education</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">Bachelor of Science in Computer Science</h3>
                <h4 className="text-lg text-blue-600 mb-1">George Mason University</h4>
                <p className="text-gray-600">Graduated 2020</p>
                <p className="text-gray-700 mt-2">
                  Completed internships at technology companies focusing on 
                  infrastructure and systems programming.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-8">
            <div className="flex items-center mb-6">
              <Award size={24} className="text-blue-500 mr-3" />
              <h2 className="text-2xl font-bold text-gray-800">Certifications</h2>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-1">AWS Certified Developer - Associate</h3>
                <p className="text-gray-600">Valid through 2025</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Core Values Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">My Approach</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="mx-auto p-3 bg-blue-100 rounded-full inline-block mb-4">
                <Cpu className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Automation First</h3>
              <p className="text-gray-700">
                I believe in automating everything that can be automated. This not only saves time but 
                also reduces errors and creates consistent, repeatable processes.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="mx-auto p-3 bg-blue-100 rounded-full inline-block mb-4">
                <Users className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Collaborative Problem Solving</h3>
              <p className="text-gray-700">
                The best solutions come from diverse perspectives. I value collaboration and believe 
                that bringing different viewpoints together leads to more robust and innovative solutions.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="mx-auto p-3 bg-blue-100 rounded-full inline-block mb-4">
                <Award className="text-blue-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Quality & Security</h3>
              <p className="text-gray-700">
                I prioritize building systems that are not only functional but also maintainable, 
                secure, and resilient. Quality and security are built-in, not added on.
              </p>
            </div>
          </div>
        </div>
        
        {/* Call to Action */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Let's Work Together</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            I'm always open to discussing new projects, opportunities, and collaborations. 
            Feel free to reach out if you'd like to connect!
          </p>
          <div className="flex justify-center space-x-4">
            <a 
              href="/contact" 
              className="bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:bg-blue-700 transition duration-300 flex items-center"
            >
              Contact Me <ArrowRight className="ml-2" size={16} />
            </a>
            <a 
              href="/projects" 
              className="bg-white border border-gray-300 text-gray-700 font-semibold px-6 py-3 rounded-lg shadow-lg hover:bg-gray-50 transition duration-300"
            >
              View My Work
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;